<template>
    <jet-action-section>
        <template #title>{{ $t("profile.browser_sessions.title") }}</template>

        <template #description>{{
            $t("profile.browser_sessions.description")
        }}</template>

        <template #content>
            <!-- Other Browser Sessions -->
            <div v-if="sessions.length > 0" class="space-y-2">
                <div
                    v-for="(session, i) in sessions"
                    :key="i"
                    class="rounded-lg border-iron-200 p-4 flex border"
                >
                    <div
                        class="h-10 w-10 rounded-lg border-iron-300 flex items-center justify-center border"
                    >
                        <svg
                            v-if="session.agent.is_desktop"
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-laptop-minimal text-[#53B1FD]"
                        >
                            <rect
                                width="18"
                                height="12"
                                x="3"
                                y="4"
                                rx="2"
                                ry="2"
                            />
                            <line x1="2" x2="22" y1="20" y2="20" />
                        </svg>

                        <svg
                            v-else
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="lucide lucide-tablet-smartphone text-[#53B1FD]"
                        >
                            <rect width="10" height="14" x="3" y="8" rx="2" />
                            <path
                                d="M5 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2h-2.4"
                            />
                            <path d="M8 18h.01" />
                        </svg>
                    </div>

                    <div class="ml-3 flex-1">
                        <div class="text-sm font-medium text-iron-600">
                            {{ session.agent.platform }} -
                            {{ session.agent.browser }}
                        </div>
                        <div>
                            <div
                                class="text-xs text-iron-500 tracking-[-0.01em]"
                            >
                                {{ session.ip_address }}
                            </div>
                        </div>
                    </div>

                    <div class="ml-auto">
                        <div
                            v-if="session.is_current_device"
                            class="gap-1 rounded-lg border-iron-300 px-2 text-xs font-medium text-iron-700 flex items-center border py-[3px]"
                        >
                            <div
                                class="h-[6px] w-[6px] shrink-0 rounded-full bg-[#12B76A]"
                            ></div>
                            {{ $t("profile.browser_sessions.this_device") }}
                        </div>
                        <div
                            v-else
                            class="gap-1 rounded-lg border-iron-300 px-2 text-xs font-medium text-iron-700 flex items-center border py-[3px]"
                        >
                            {{ session.last_active }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="mt-5 flex items-center justify-end">
                <Button
                    @click="confirmLogout"
                    size="sm"
                    :recently-successful="form.recentlySuccessful"
                    >{{
                        $t(
                            "profile.browser_sessions.logout_other_browser_sessions",
                        )
                    }}</Button
                >
            </div>

            <!-- Log Out Other Devices Confirmation Modal -->
            <Dialog v-model:open="confirmingLogout">
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {{
                                $t(
                                    "profile.browser_sessions.logout_other_browser_sessions",
                                )
                            }}
                        </DialogTitle>
                        <DialogDescription>
                            {{ $t("profile.browser_sessions.enter_password") }}
                        </DialogDescription>
                    </DialogHeader>
                    <div class="mt-4 px-6">
                        <Label for="password">
                            {{ $t("profile.browser_sessions.password") }}
                        </Label>
                        <Input
                            ref="password"
                            v-model="form.password"
                            type="password"
                            id="password"
                            class="mt-1"
                            @keyup.enter="logoutOtherBrowserSessions"
                        />
                        <jet-input-error
                            :message="form.errors.password"
                            class="mt-2"
                        />
                    </div>
                    <DialogFooter>
                        <DialogClose>
                            <Button variant="outline">
                                {{ $t("cancel") }}</Button
                            >
                        </DialogClose>
                        <Button
                            class="ml-2"
                            :loading="form.processing"
                            @click="logoutOtherBrowserSessions"
                        >
                            {{ $t("confirm") }}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </template>
    </jet-action-section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useForm } from "@inertiajs/vue3";
// @ts-expect-error
import JetActionSection from "../../Jetstream/ActionSection.vue";

// @ts-expect-error
import JetInputError from "../../Jetstream/InputError.vue";
import Button from "@/components/ui/button/Button.vue";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog-v2";
import route from "ziggy-js";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DialogClose from "@/components/ui/dialog/DialogClose.vue";

interface Props {
    sessions: any[];
}

defineProps<Props>();
const confirmingLogout = ref(false);

const form = useForm({
    password: "",
});

const confirmLogout = () => {
    confirmingLogout.value = true;
};

const logoutOtherBrowserSessions = () => {
    form.delete(route("other-browser-sessions.destroy"), {
        preserveScroll: true,
        onSuccess: closeModal,
        onFinish: () => form.reset(),
    });
};

const closeModal = () => {
    confirmingLogout.value = false;
    form.reset();
};
</script>
